import axios from 'axios'
import type { AxiosRequestConfig, Method, AxiosError } from 'axios'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { checkAndRedirectIfAnotherUserLoggedIn } from '@/utils/userSessionGuard'

const BASE_URL = (import.meta.env.VITE_BASE_URL + import.meta.env.VITE_API_PREFIX) as string

/**
 * callApi で許容する送信データ型
 * - GET/DELETE: params として送る
 * - POST/PUT/PATCH: data(body) として送る
 * - multipart/form-data: FormData を data(body) として送る（axiosが適切に Content-Type/boundary を付与）
 */
type ApiPayload = Record<string, unknown> | FormData | undefined | null

/**
 * 汎用API呼び出し関数
 * GET/POST/PUT/PATCH/DELETE などのHTTPメソッドに対応。
 * 型パラメータで送信データとレスポンスの型を指定可能。
 *
 * @template TResponse APIから返却されるデータの型
 * @template TRequest APIに送信するデータの型（object / FormData / undefined）
 * @param endpoint APIのエンドポイント (例: '/user-info', '/flight-routes')
 * @param method HTTPメソッド。デフォルトは 'GET'
 * @param data 送信データ。
 *   - GET/DELETE の場合はクエリパラメータ（params）として送信
 *   - POST/PUT/PATCH の場合はリクエストボディ（data）として送信
 *   - FormData を渡すと multipart/form-data として送信（axiosが自動付与）
 * @param baseUrl APIのURL（省略時は BASE_URL）
 * @returns APIのレスポンスデータ
 */
export async function callApi<TResponse = unknown, TRequest extends ApiPayload = undefined>(
  endpoint: string,
  method: Method = 'GET',
  data?: TRequest,
  baseUrl?: string,
): Promise<TResponse> {
  if (
    (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') &&
    endpoint !== '/logout'
  ) {
    // データ更新を伴う場合、他タブで他ユーザでログインされていないかチェック
    const isAnotherUserLoggedIn = await checkAndRedirectIfAnotherUserLoggedIn()
    if (isAnotherUserLoggedIn) {
      throw new Error('別ユーザでログイン')
    }
  }

  const { handleApiError } = useErrorHandler()
  const config: AxiosRequestConfig = {
    url: `${baseUrl ?? BASE_URL}${endpoint}`,
    method,
    withCredentials: true,
    headers: {
      Accept: 'application/json',
    },
  }

  /**
   * GET/DELETE は params、それ以外は data(body) に入れる
   * - data が FormData の場合も body として送るのでここでOK
   */
  if (method === 'GET' || method === 'DELETE') {
    // data が FormData の場合は params にできないので無視する（設計上、GETでFormDataは想定しない）
    if (data && typeof data === 'object' && !(data instanceof FormData)) {
      config.params = data
    }
  } else {
    config.data = data
  }

  try {
    const response = await axios(config)
    return response.data as TResponse
  } catch (err: unknown) {
    const error = err as AxiosError<any>

    /**
     * 422（バリデーション）だけは「共通エラー処理を通さず、呼び出し側に返す」
     * - API仕様で { message_ids: string[] } を返すようなケースを想定
     */
    if (error.response?.status === 422) {
      return error.response.data as TResponse
    }

    /**
     * それ以外は共通エラー処理へ
     * - 401 のときにログアウト誘導を出す
     * - 500 のときに汎用エラー表示
     * などは useErrorHandler 側で一元管理
     */
    await handleApiError(error)
    throw error
  }
}
